import React from 'react';
import {
    AddContainer,
    Component4,
    Texts,
    Component2,
    InnerComponent,
    RemoveButton,
    SaveButton,
    LabelWithTooltip
} from './Style';
import { Formik, Form, FieldArray } from 'formik';
import InputSelect from 'src/components/shared/Form/Select';
import DateTimePicker from 'src/components/App/DatePicker';
import theme from 'src/config/theme';
import { Box } from '@mui/material';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { ReactComponent as Remove } from '../../../../../assets/images/close-circle.svg';
import { getUserFromToken } from 'src/DecodeToken';
import { ReactComponent as Add } from '../../../../../assets/images/add-square.svg';
import useResponsive from 'src/components/hooks/useResponsive';

dayjs.extend(customParseFormat);

const Days = [
    { label: 'Monday', value: 'monday' },
    { label: 'Tuesday', value: 'tuesday' },
    { label: 'Wednesday', value: 'wednesday' },
    { label: 'Thursday', value: 'thursday' },
    { label: 'Friday', value: 'friday' },
    { label: 'Saturday', value: 'saturday' },
    { label: 'Sunday', value: 'sunday' },
];

const parseToDate = (timeInput) => {
    if (!timeInput) return null;
    const d = dayjs(timeInput);
    if (d.isValid()) return d.toDate();
    const shortD = dayjs(`2026-01-01 ${timeInput}`, 'YYYY-MM-DD HH:mm');
    return shortD.isValid() ? shortD.toDate() : null;
};

const ClinicTiming = ({ data = [], innerRef, setClinicProfileData }) => {
    const { isMobile, isTablet } = useResponsive();
    const Width = isMobile || isTablet ? '18px' : '24px';
    const Height = isMobile || isTablet ? '18px' : '24px';
    const userId = React.useMemo(() => getUserFromToken(), []);

    // Stabilize initial values with data to avoid manual useEffect sync
    const initialValues = React.useMemo(() => {
        try {
            if (Array.isArray(data) && data.length > 0) {
                // SAFETY: Remove duplicates and limit to 50 items to prevent browser crash
                const seen = new Set();
                const uniqueData = data.filter(item => {
                    const key = `${item?.day}-${item?.startTime}-${item?.endTime}`;
                    if (seen.has(key)) return false;
                    seen.add(key);
                    return true;
                }).slice(0, 50);

                console.log(`>>> [DEBUG] Filtered clinicTiming from ${data.length} to ${uniqueData.length} unique items`);

                return {
                    clinicTiming: uniqueData.map(item => ({
                        day: item?.day || '',
                        startTime: parseToDate(item?.startTime),
                        endTime: parseToDate(item?.endTime),
                        user: userId,
                    }))
                };
            }
        } catch (error) {
            console.error(">>> [DEBUG] Error in initialValues useMemo:", error);
        }
        return {
            clinicTiming: [{ day: '', startTime: null, endTime: null, user: userId }]
        };
    }, [data.length, userId]); // Use data.length instead of stringify for performance with 3000+ items

    const handleSubmit = (values) => {
        const formattedTiming = values.clinicTiming.map(item => ({
            ...item,
            startTime: item.startTime ? dayjs(item.startTime).toISOString() : null,
            endTime: item.endTime ? dayjs(item.endTime).toISOString() : null,
        }));

        setClinicProfileData(prev => ({
            ...prev,
            clinicTiming: formattedTiming,
        }));
    };

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            innerRef={innerRef}
            enableReinitialize={true}
        >
            {({ values, setFieldValue, handleChange }) => {
                return (
                    <Form>
                        <AddContainer sx={{ gap: '20px', paddingLeft: isMobile || isTablet ? 'auto' : '0 !important', paddingRight: isMobile || isTablet ? 'auto' : '0 !important' }}>
                            <FieldArray name="clinicTiming">
                                {({ push, remove }) => (
                                    <>
                                        {values.clinicTiming && values.clinicTiming.map((timing, index) => (
                                            <Component2 key={index} sx={{ alignItems: 'center' }}>
                                                <InnerComponent>
                                                    <Texts>Day</Texts>
                                                    <InputSelect
                                                        name={`clinicTiming.${index}.day`}
                                                        options={Days}
                                                        value={timing.day || ''}
                                                        onChange={(val) => setFieldValue(`clinicTiming.${index}.day`, val)}
                                                        placeholder="Select"
                                                        variant="outlined"
                                                        sx={{
                                                            boxShadow: theme.shadows[1],
                                                            '& .MuiOutlinedInput-root': {
                                                                height: '52px',
                                                                borderRadius: '12px',
                                                            },
                                                        }}
                                                    />
                                                </InnerComponent>
                                                <InnerComponent>
                                                    <Texts>Start Time</Texts>
                                                    <DateTimePicker
                                                        type="time"
                                                        value={timing.startTime}
                                                        onChange={(val) => setFieldValue(`clinicTiming.${index}.startTime`, val)}
                                                    />
                                                </InnerComponent>
                                                <InnerComponent>
                                                    <Texts>End Time</Texts>
                                                    <DateTimePicker
                                                        type="time"
                                                        value={timing.endTime}
                                                        onChange={(val) => setFieldValue(`clinicTiming.${index}.endTime`, val)}
                                                    />
                                                </InnerComponent>
                                                <RemoveButton onClick={() => remove(index)} sx={{ mt: isMobile ? "5px" : 3 }}>
                                                    <Remove width={Width} height={Height} />
                                                </RemoveButton>
                                            </Component2>
                                        ))}
                                        <Box display="flex" justifyContent="flex-end" mt={2}>
                                            <SaveButton
                                                variant="outlined"
                                                onClick={() => push({ day: '', startTime: null, endTime: null, user: userId })}
                                                startIcon={<Add width={Width} height={Height} sx={{ color: theme.palette.primary.main }} />}
                                            >Add </SaveButton>
                                        </Box>
                                    </>
                                )}
                            </FieldArray>
                        </AddContainer>
                    </Form >
                );
            }}
        </Formik >
    );
};

export default React.memo(ClinicTiming);
